import React, { useState, useEffect } from 'react';
import { Button } from '../ui/button.tsx';
import { Input } from '../ui/input.tsx';
import { Label } from '../ui/label.tsx';
import { useUpdateSession, useGetSession } from '../../api/session/sessionQueries';
import { toast } from 'react-toastify';
import { SlidePanel } from '../ui/slide-panel';
import { LoadingSpinner } from '../ui/loading-spinner.tsx';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover.tsx';
import { Calendar } from '../ui/calendar.tsx';
import { format } from 'date-fns';

export function SessionEditPanel({ session, isOpen, onClose }) {
  const { mutate: updateSession, isPending: isUpdating } = useUpdateSession();
  const { data: sessionData, isLoading: isLoadingSession } = useGetSession(session?.sessionId);
  const [sessionId, setSessionId] = useState(null);
  const [name, setName] = useState("");
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);

  useEffect(() => {
    if (sessionData) {
      setSessionId(sessionData.sessionId);
      setName(sessionData.name || "");
      setStartDate(sessionData.startDate ? new Date(sessionData.startDate) : null);
      setEndDate(sessionData.endDate ? new Date(sessionData.endDate) : null);
    } else if (session) {
      const id = session.sessionId;
      if (!id) {
        console.error('No valid session ID found:', session);
        toast.error('Invalid session data');
        onClose();
        return;
      }
      setSessionId(id);
      setName(session.name || "");
      try {
        const start = session.startDate ? new Date(session.startDate) : null;
        const end = session.endDate ? new Date(session.endDate) : null;
        
        if (start && isNaN(start.getTime())) throw new Error('Invalid start date');
        if (end && isNaN(end.getTime())) throw new Error('Invalid end date');
        
        setStartDate(start);
        setEndDate(end);
      } catch (error) {
        console.error('Error parsing session dates:', error);
      }
    }
  }, [session, sessionData, onClose]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!sessionId) {
      toast.error('Session ID is required');
      return;
    }
    if (!name) {
      toast.error('Session name is required');
      return;
    }
    if (!startDate || !endDate) {
      toast.error('Both start and end dates are required');
      return;
    }
    if (startDate > endDate) {
      toast.error('Start date cannot be after end date');
      return;
    }

    try {
      await updateSession({
        sessionId,
        data: {
          name,
          startDate,
          endDate
        }
      });
      toast.success('Session updated successfully');
      onClose();
    } catch (error) {
      toast.error(error.message || 'Failed to update session');
    }
  };

  if (isLoadingSession) {
    return (
      <SlidePanel isOpen={isOpen} onClose={onClose}>
        <div className="flex h-full items-center justify-center">
          <LoadingSpinner size="lg" />
        </div>
      </SlidePanel>
    );
  }

  return (
    <SlidePanel isOpen={isOpen} onClose={onClose}>
      <div className="space-y-4 p-4">
        <h2 className="text-lg font-semibold">Edit Session</h2>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="name">Session Name</Label>
            <Input
              id="name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Enter session name"
            />
          </div>

          <div className="space-y-2">
            <Label>Start Date</Label>
            <Popover>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  className={`w-full justify-start text-left font-normal ${
                    !startDate && "text-muted-foreground"
                  }`}
                >
                  {startDate ? format(startDate, "PPP") : "Pick a date"}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0" align="start">
                <Calendar
                  mode="single"
                  selected={startDate}
                  onSelect={setStartDate}
                  initialFocus
                />
              </PopoverContent>
            </Popover>
          </div>

          <div className="space-y-2">
            <Label>End Date</Label>
            <Popover>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  className={`w-full justify-start text-left font-normal ${
                    !endDate && "text-muted-foreground"
                  }`}
                >
                  {endDate ? format(endDate, "PPP") : "Pick a date"}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0" align="start">
                <Calendar
                  mode="single"
                  selected={endDate}
                  onSelect={setEndDate}
                  initialFocus
                />
              </PopoverContent>
            </Popover>
          </div>

          <div className="flex justify-end gap-2">
            <Button type="button" variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" disabled={isUpdating}>
              {isUpdating ? <LoadingSpinner className="mr-2" /> : null}
              Save Changes
            </Button>
          </div>
        </form>
      </div>
    </SlidePanel>
  );
}